import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api_clients';
import { useOrganizationStore } from '../stores/organization-store';
import { useRBAC } from '../../hooks/use-rbac';
import {
  mapApiFleetrockMatchResponseToEntity,
  mapApiFleetrockUnmatchedUnitToEntity,
  mapApiFleetrockSyncResultToEntity,
  mapApiFleetrockManualMatchToEntity,
  mapApiVMRSCodesResponseToEntity,
} from '../mappers/fleetrock.mapper';
import type {
  FleetrockStatus,
  FleetrockMatchResponse,
  FleetrockUnmatchedUnit,
  FleetrockSyncResult,
  FleetrockManualMatchResult,
  VMRSCodesResponse,
} from '../../types/fleetrock';
import type {
  StoreFleetrockCredentialsRequest,
  ManualMatchFleetrockUnitRequest,
} from '../api_clients/api/api';

// ==========================================
// QUERY KEYS
// ==========================================

export const fleetrockKeys = {
  all: ['fleetrock'] as const,
  status: (orgId: string) => [...fleetrockKeys.all, 'status', orgId] as const,
  unmatchedUnits: (orgId: string) => [...fleetrockKeys.all, 'unmatched-units', orgId] as const,
  vmrsCodes: (system?: string) => ['vmrs-codes', system] as const,
};

// ==========================================
// STATUS HOOK
// ==========================================

export function useFleetrockStatus() {
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useQuery({
    queryKey: fleetrockKeys.status(orgUnid),
    queryFn: async (): Promise<FleetrockStatus | null> => {
      if (!orgUnid) return null;
      const response = await apiClient.fleetrockApi.getFleetrockStatus(orgUnid, '');
      const data = response.data?.data;
      if (!data) return null;
      return {
        connected: data.connected || false,
        connectedAt: data.connectedAt || undefined,
        label: data.label || undefined,
      };
    },
    enabled: !!orgUnid && isAdminOrOwner,
  });
}

// ==========================================
// CREDENTIAL HOOKS
// ==========================================

export function useStoreFleetrockCredentials() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (data: { username: string; apiKey: string; label?: string }) => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      const request: StoreFleetrockCredentialsRequest = {
        usernameCustomer: data.username,
        apiKeyCustomer: data.apiKey,
        label: data.label,
      };
      const response = await apiClient.fleetrockApi.storeFleetrockCredentials(orgUnid, '', request);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fleetrockKeys.all });
      queryClient.invalidateQueries({ queryKey: ['settings', 'integrations'] });
    },
  });
}

export function useDeleteFleetrockCredentials() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async () => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      const response = await apiClient.fleetrockApi.deleteFleetrockCredentials(orgUnid, '');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fleetrockKeys.all });
      queryClient.invalidateQueries({ queryKey: ['settings', 'integrations'] });
    },
  });
}

// ==========================================
// UNIT MATCHING HOOKS
// ==========================================

export function useMatchFleetrockUnits() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (): Promise<FleetrockMatchResponse | null> => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      const response = await apiClient.fleetrockApi.matchFleetrockUnits(orgUnid, '', {});
      const data = response.data?.data;
      if (!data) return null;
      return mapApiFleetrockMatchResponseToEntity(data);
    },
    onSuccess: () => {
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: fleetrockKeys.unmatchedUnits(orgUnid) });
      }
    },
  });
}

export function useUnmatchedFleetrockUnits() {
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useQuery({
    queryKey: fleetrockKeys.unmatchedUnits(orgUnid),
    queryFn: async (): Promise<{ units: FleetrockUnmatchedUnit[]; count: number } | null> => {
      if (!orgUnid) return null;
      const response = await apiClient.fleetrockApi.listUnmatchedFleetrockUnits(orgUnid, '');
      const data = response.data?.data;
      if (!data) return null;
      return {
        units: (data.units || []).map(mapApiFleetrockUnmatchedUnitToEntity),
        count: data.count || 0,
      };
    },
    enabled: !!orgUnid && isAdminOrOwner,
  });
}

export function useManualMatchFleetrockUnit() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (data: { fleetrockId: string; truckUnid: string }): Promise<FleetrockManualMatchResult | null> => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      const request: ManualMatchFleetrockUnitRequest = {
        fleetrockId: data.fleetrockId,
        truckUnid: data.truckUnid,
      };
      const response = await apiClient.fleetrockApi.manualMatchFleetrockUnit(orgUnid, '', request);
      const resData = response.data?.data;
      if (!resData) return null;
      return mapApiFleetrockManualMatchToEntity(resData);
    },
    onSuccess: () => {
      if (orgUnid) {
        queryClient.invalidateQueries({ queryKey: fleetrockKeys.unmatchedUnits(orgUnid) });
      }
    },
  });
}

// ==========================================
// SYNC HOOKS
// ==========================================

export function useSyncFleetrockRepairOrders() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (): Promise<FleetrockSyncResult | null> => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      const response = await apiClient.fleetrockApi.syncFleetrockRepairOrders(orgUnid, '', {});
      const data = response.data?.data;
      if (!data) return null;
      return mapApiFleetrockSyncResultToEntity(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      queryClient.invalidateQueries({ queryKey: fleetrockKeys.all });
    },
  });
}

export function useSyncFleetrockMaintenance() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (): Promise<FleetrockSyncResult | null> => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      const response = await apiClient.fleetrockApi.syncFleetrockMaintenance(orgUnid, '', {});
      const data = response.data?.data;
      if (!data) return null;
      return mapApiFleetrockSyncResultToEntity(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      queryClient.invalidateQueries({ queryKey: fleetrockKeys.all });
    },
  });
}

export function useSyncFleetrockParts() {
  const queryClient = useQueryClient();
  const { organization } = useOrganizationStore();
  const { isAdminOrOwner } = useRBAC();
  const orgUnid = organization?.id || '';

  return useMutation({
    mutationFn: async (): Promise<FleetrockSyncResult | null> => {
      if (!isAdminOrOwner) throw new Error('Permission denied: admin access required');
      if (!orgUnid) throw new Error('Organization not found');
      const response = await apiClient.fleetrockApi.syncFleetrockParts(orgUnid, '', {});
      const data = response.data?.data;
      if (!data) return null;
      return mapApiFleetrockSyncResultToEntity(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parts'] });
      queryClient.invalidateQueries({ queryKey: fleetrockKeys.all });
    },
  });
}

// ==========================================
// VMRS CODES HOOK
// ==========================================

export function useVMRSCodes(system?: string) {
  return useQuery({
    queryKey: fleetrockKeys.vmrsCodes(system),
    queryFn: async (): Promise<VMRSCodesResponse | null> => {
      const response = await apiClient.vmrsApi.listVMRSCodes('', system);
      const data = response.data?.data;
      if (!data) return null;
      return mapApiVMRSCodesResponseToEntity(data);
    },
  });
}
